/**
 * 시뮬레이터 탭(만공차/조명/비상벨) 공용 "자동 실행" 헬퍼.
 * 각 탭이 항목을 하나씩 순회하며 dry-run 캡처 이벤트로 결과를 검증하는 데 사용한다.
 */
import { LightsSimulatorService, type SimEvent } from '../../services/LightsSimulatorService';

export const sleep = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

/** 자동 테스트 리포트 한 줄(대상 이름 + 통과 여부 + 상세 설명). */
export interface AutoCheckResult {
  name: string;
  passed: boolean;
  detail: string;
  /** 통합 자동 테스트에서 여러 시뮬레이터 결과를 합칠 때의 구분 태그(조명/만공차등/비상벨). 개별 탭 단독 사용 시엔 비워둔다. */
  category?: string;
}

/** 조명/만공차등/비상벨 탭이 통합 자동 테스트(SimulatorPage index.tsx)에 공통으로 노출하는 핸들. */
export interface SimAutoRunHandle {
  /** 그 탭의 기존 "자동 실행" 판정 루프를 그대로 돌리고 결과를 반환한다(로컬 리포트 다이얼로그는 띄우지 않음). */
  runAutoCheck: () => Promise<AutoCheckResult[]>;
  /** 진행 중인 자동 실행을 중단 요청한다(기존 "정지" 버튼과 동일한 stopRequestedRef). */
  stopAutoCheck: () => void;
}

/**
 * cursor 이후의 캡처 이벤트에서 predicate 를 만족하는 것이 나타날 때까지 짧게 폴링한다.
 * 트리거(모션/비상호출) 응답이 성공 여부만 알려주고 실제 fan-out 결과는 알려주지 않으므로,
 * "조명이 실제로 켜졌는가" 를 확인하려면 이벤트를 직접 조회해야 한다.
 */
export async function waitForLightEvent(
  cursor: number,
  predicate: (ev: SimEvent) => boolean,
  timeoutMs = 2500,
  pollMs = 300,
): Promise<{ hit: SimEvent | null; cursor: number }> {
  let c = cursor;
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const resp = await LightsSimulatorService.getEvents(c);
    if (resp.cursor > c) c = resp.cursor;
    const hit = resp.events.find(predicate);
    if (hit) return { hit, cursor: c };
    await sleep(pollMs);
  }
  return { hit: null, cursor: c };
}
